import { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";

import {
  IEMedIntegrationCount,
  IEMedLabResult,
  IEMedMedication,
  IEMedPatientDetails,
} from "../../../@types/admin/AdminEMedIntegration";
import { TableSorted } from "../../../components/TableSorted";
import {
  useEMedLabResults,
  useEMedMedications,
} from "../../../hooks/queries/admin/eMedIntegration";
import {
  getLongFormattedDate,
  getShortFormattedUtcDate,
} from "../../../utils/dateFormatter";
import { formatMeasurementCode } from "../../../utils/measurementNames";
import {
  AdminBreadcrumb,
  ErrorMessage,
  IAdminBreadcrumbItem,
  ModalContainer,
  ModalHeader,
  ModalSubtitle,
  ModalTitle,
  TableContainer,
  TableLoadingState,
  TableWrapper,
} from "../shared";

/** Which feed the selected summary row belongs to. */
export const EMED_LAB_RESULT_PROCESS = "EMED-LAB-RESULT";
export const EMED_MEDICATION_PROCESS = "EMED-MEDICATION";

interface IEMedDrillDownModalProps {
  row: IEMedIntegrationCount;
}

/** One queued message, flattened to the fields both feeds share. */
interface IMessageRow {
  integrationQueueId: string;
  created: string;
  patientDetails: IEMedPatientDetails;
  itemCount: number;
  labResults: IEMedLabResult[];
  medications: IEMedMedication[];
}

function patientName(details: IEMedPatientDetails): string {
  const name = [details.firstName, details.lastName].filter(Boolean).join(" ");
  return name || "Unknown patient";
}

/**
 * One day's eMED messages and their contents (EN-5160).
 *
 * Retool used five modal frames for this — two top-level modals keyed off the
 * row's `processName`, each with its own expanded-row table, plus the summary
 * itself. The portal has a single global modal slot, so per the EN-5149 rule
 * this is one modal that navigates its own levels with a breadcrumb: the
 * date's messages, then one message's lab results or medications.
 *
 * Consolidating into tabs was the other option the ticket offered and was not
 * taken: a summary row is already either a lab-result row or a medication row
 * (`processName`), so a tab strip would always have one empty half.
 */
export function EMedDrillDownModal({ row }: IEMedDrillDownModalProps) {
  const isLabResults = row.processName === EMED_LAB_RESULT_PROCESS;
  const { date } = row;

  const {
    labResults,
    isLoading: labResultsLoading,
    isError: labResultsError,
  } = useEMedLabResults(isLabResults ? date : undefined);
  const {
    medications,
    isLoading: medicationsLoading,
    isError: medicationsError,
  } = useEMedMedications(isLabResults ? undefined : date);

  const [selectedQueueId, setSelectedQueueId] = useState<string | null>(null);

  const isLoading = isLabResults ? labResultsLoading : medicationsLoading;
  const isError = isLabResults ? labResultsError : medicationsError;

  const messages: IMessageRow[] = useMemo(() => {
    if (isLabResults) {
      return labResults.map((message) => ({
        integrationQueueId: message.integrationQueueId,
        created: message.created,
        patientDetails: message.patientDetails,
        itemCount: message.labResults?.length ?? 0,
        labResults: message.labResults ?? [],
        medications: [],
      }));
    }
    return medications.map((message) => ({
      integrationQueueId: message.integrationQueueId,
      created: message.created,
      patientDetails: message.patientDetails,
      itemCount: message.medications?.length ?? 0,
      labResults: [],
      medications: message.medications ?? [],
    }));
  }, [isLabResults, labResults, medications]);

  const selected = messages.find(
    (message) => message.integrationQueueId === selectedQueueId
  );

  // Same UTC-calendar rendering as the summary column it drills down from.
  const dateLabel = getShortFormattedUtcDate(date);

  const messageColumns: ColumnDef<IMessageRow, string>[] = useMemo(
    () => [
      {
        id: "patientName",
        header: "Patient",
        accessorFn: (message) => patientName(message.patientDetails),
        enableSorting: true,
      },
      {
        id: "mrn",
        header: "MRN",
        accessorFn: (message) => message.patientDetails.mrn ?? "",
        enableSorting: true,
      },
      {
        accessorKey: "created",
        header: "Received",
        cell: ({ row: messageRow }) => (
          <span>{getLongFormattedDate(messageRow.original.created, "-")}</span>
        ),
        enableSorting: true,
      },
      {
        id: "itemCount",
        header: isLabResults ? "Lab results" : "Medications",
        // Rendered as text: TableSorted dashes a falsy value, and a message
        // that arrived carrying nothing is exactly what an operator is
        // looking for.
        accessorFn: (message) => String(message.itemCount),
        enableSorting: true,
      },
      {
        accessorKey: "integrationQueueId",
        header: "Integration queue ID",
        enableSorting: true,
      },
    ],
    [isLabResults]
  );

  const labResultColumns: ColumnDef<IEMedLabResult, string>[] = useMemo(
    () => [
      {
        id: "measurementName",
        header: "Measurement",
        // Retool's getMeasurementName.js, shared with Jupyter Integration and
        // Measurements — already ported to utils/measurementNames.ts.
        accessorFn: (result) => formatMeasurementCode(result.snomedCode),
        enableSorting: true,
      },
      { accessorKey: "snomedCode", header: "SNOMED code", enableSorting: true },
      {
        accessorKey: "observationValue",
        header: "Value",
        enableSorting: true,
      },
      { accessorKey: "unit", header: "Unit", enableSorting: true },
      { accessorKey: "range", header: "Range", enableSorting: true },
      {
        accessorKey: "observationDate",
        header: "Observed",
        cell: ({ row: resultRow }) => (
          <span>
            {getLongFormattedDate(resultRow.original.observationDate, "-")}
          </span>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "observationResultStatus",
        header: "Result status",
        enableSorting: true,
      },
    ],
    []
  );

  const medicationColumns: ColumnDef<IEMedMedication, string>[] = useMemo(
    () => [
      { accessorKey: "name", header: "Name", enableSorting: true },
      { accessorKey: "snomedCode", header: "SNOMED code", enableSorting: true },
      { accessorKey: "route", header: "Route", enableSorting: true },
      {
        id: "dose",
        header: "Dose",
        accessorFn: (medication) =>
          medication.dose === null || medication.dose === undefined
            ? "-"
            : `${medication.dose}${
                medication.unit ? ` ${medication.unit}` : ""
              }`,
        enableSorting: true,
      },
      { accessorKey: "regime", header: "Regime", enableSorting: true },
      {
        accessorKey: "startDate",
        header: "Start date",
        cell: ({ row: medicationRow }) => (
          <span>
            {getLongFormattedDate(medicationRow.original.startDate, "-")}
          </span>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "endDate",
        header: "End date",
        cell: ({ row: medicationRow }) => (
          <span>
            {getLongFormattedDate(medicationRow.original.endDate, "-")}
          </span>
        ),
        enableSorting: true,
      },
    ],
    []
  );

  const breadcrumb: IAdminBreadcrumbItem[] = [
    { label: dateLabel, onClick: () => setSelectedQueueId(null) },
  ];
  if (selected) {
    breadcrumb.push({
      label: `${patientName(selected.patientDetails)}${
        selected.patientDetails.mrn ? ` · ${selected.patientDetails.mrn}` : ""
      }`,
    });
  }

  const title = isLabResults
    ? "Patient lab results"
    : "Patient medication history";

  return (
    <ModalContainer>
      <ModalHeader>
        <ModalTitle>{title}</ModalTitle>
        <AdminBreadcrumb items={breadcrumb} ariaLabel="Drill-down" />
        <ModalSubtitle>
          {row.hospitalName ?? "Unknown hospital"} · {row.count}{" "}
          {row.count === 1 ? "message" : "messages"} on this date
        </ModalSubtitle>
      </ModalHeader>

      {isError && (
        <ErrorMessage>
          Failed to load this date&apos;s{" "}
          {isLabResults ? "lab results" : "medications"}. Close the dialog and
          try again.
        </ErrorMessage>
      )}

      <TableContainer>
        <TableWrapper
          refreshKeys={[["admin-emed-lab-results"], ["admin-emed-medications"]]}
        >
          {selected ? (
            <TableSorted
              columns={
                isLabResults
                  ? (labResultColumns as ColumnDef<any, any>[])
                  : (medicationColumns as ColumnDef<any, any>[])
              }
              data={isLabResults ? selected.labResults : selected.medications}
              emptyStateText={
                isLabResults
                  ? "This message carried no lab results"
                  : "This message carried no medications"
              }
            />
          ) : (
            <TableSorted
              columns={messageColumns as ColumnDef<any, any>[]}
              data={messages}
              onRowClick={(message: IMessageRow) =>
                setSelectedQueueId(message.integrationQueueId)
              }
              emptyStateText={
                isLoading ? <TableLoadingState /> : "No rows found"
              }
            />
          )}
        </TableWrapper>
      </TableContainer>
    </ModalContainer>
  );
}
